import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, FileUp, Save, Send } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import {
  EPK_VARIANT_LABELS,
  type DraftRecord,
} from '@/lib/epk-draft-types'
import {
  archiveEpkDraft,
  authoriseEpkDraft,
  createEpkDraft,
  getEpkDraft,
  previewDraftHtml,
  presignEpkPdf,
  registerEpkPdf,
  submitEpkDraft,
  updateEpkDraft,
} from '@/lib/epk-drafts-api'
import { useAuthStore } from '@/lib/auth-store'

export function EpkDraftEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'admin'
  const canEdit = role === 'admin' || role === 'manager'

  const draftQ = useQuery<DraftRecord, ApiClientError>({
    queryKey: ['epk-draft', id],
    queryFn: () => getEpkDraft(id!),
    enabled: !!id,
  })

  const draft = draftQ.data
  const [label, setLabel] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [cssBody, setCssBody] = useState('')
  const [dirty, setDirty] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [pdfUploading, setPdfUploading] = useState(false)
  const [pdfErr, setPdfErr] = useState<string | null>(null)

  useEffect(() => {
    if (!draft) return
    setLabel(draft.label ?? '')
    setHtmlBody(draft.htmlBody)
    setCssBody(draft.cssBody)
    setDirty(false)
  }, [draft?.id, draft?.updatedAt])

  const previewSrc = useMemo(
    () => previewDraftHtml(htmlBody, cssBody),
    [htmlBody, cssBody],
  )

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: { htmlBody: string; cssBody: string; label?: string } = {
        htmlBody,
        cssBody,
      }
      if (label) body.label = label
      return updateEpkDraft(id!, body)
    },
    onSuccess: () => {
      setDirty(false)
      setSaveErr(null)
      qc.invalidateQueries({ queryKey: ['epk-draft', id] })
      qc.invalidateQueries({ queryKey: ['epk-drafts'] })
    },
    onError: (err) =>
      setSaveErr(err instanceof ApiClientError ? err.message : 'Save failed'),
  })

  async function runAction(
    fn: () => Promise<DraftRecord>,
    successMsg?: string,
  ) {
    try {
      if (dirty && draft?.status === 'draft') {
        await saveMutation.mutateAsync()
      }
      await fn()
      await qc.invalidateQueries({ queryKey: ['epk-draft', id] })
      await qc.invalidateQueries({ queryKey: ['epk-drafts'] })
      if (successMsg) alert(successMsg)
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Action failed')
    }
  }

  async function uploadPdf(file: File) {
    if (!id) return
    setPdfUploading(true)
    setPdfErr(null)
    try {
      const { uploadUrl, pdfS3Key, pdfFilename } = await presignEpkPdf(id, file.name, file.type)
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/pdf' },
        body: file,
      })
      if (!putRes.ok) throw new Error(`S3 upload failed (${putRes.status})`)
      await registerEpkPdf(id, { pdfS3Key, pdfFilename })
      await qc.invalidateQueries({ queryKey: ['epk-draft', id] })
    } catch (err) {
      setPdfErr(err instanceof Error ? err.message : 'PDF upload failed')
    } finally {
      setPdfUploading(false)
    }
  }

  async function newVersion() {
    if (!draft) return
    try {
      const created = await createEpkDraft({
        variant: draft.variant,
        seedFromDraftId: draft.id,
        label: `v${draft.version + 1}`,
      })
      navigate(`/settings/epks/drafts/${created.id}`)
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Failed to create version')
    }
  }

  const readOnly = !draft || draft.status !== 'draft' || !canEdit

  return (
    <div>
      <div className="mb-4">
        <Link
          to="/settings/epks"
          className="inline-flex items-center gap-1 text-sm text-ovation-muted hover:text-ovation-ink"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to EPK settings
        </Link>
      </div>

      <AsyncBoundary isLoading={draftQ.isLoading} error={draftQ.error}>
        {draft && (
          <>
            <PageHeader
              title={`${EPK_VARIANT_LABELS[draft.variant]} · v${draft.version}`}
              subtitle={
                draft.status === 'draft'
                  ? 'Edit HTML and CSS. Submit for review, then an admin authorises to unlock auto-dispatch.'
                  : `Status: ${draft.status.replace(/_/g, ' ')}`
              }
              actions={
                <div className="flex flex-wrap items-center gap-2">
                  {draft.status === 'draft' && canEdit && (
                    <>
                      <button
                        type="button"
                        disabled={!dirty || saveMutation.isPending}
                        onClick={() => saveMutation.mutate()}
                        className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                      >
                        <Save className="h-3.5 w-3.5" />
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => runAction(() => submitEpkDraft(draft.id))}
                        className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper"
                      >
                        <Send className="h-3.5 w-3.5" />
                        Submit for review
                      </button>
                    </>
                  )}
                  {(draft.status === 'draft' || draft.status === 'in_review') && isAdmin && (
                    <button
                      type="button"
                      onClick={() =>
                        runAction(
                          () => authoriseEpkDraft(draft.id),
                          'Draft authorised — auto-dispatch enabled for this variant.',
                        )
                      }
                      className="flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Authorise
                    </button>
                  )}
                  {draft.status !== 'archived' && canEdit && (
                    <button
                      type="button"
                      onClick={() => runAction(() => archiveEpkDraft(draft.id))}
                      className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium"
                    >
                      Archive
                    </button>
                  )}
                  {(draft.status === 'authorised' || draft.status === 'archived') && canEdit && (
                    <button
                      type="button"
                      onClick={newVersion}
                      className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium"
                    >
                      New version
                    </button>
                  )}
                </div>
              }
            />

            {saveErr && (
              <p className="mb-3 text-sm text-red-600">{saveErr}</p>
            )}

            <div className="mb-4">
              <label className="mb-1 block text-xs font-medium text-ovation-muted">
                Label
              </label>
              <input
                type="text"
                value={label}
                readOnly={readOnly}
                onChange={(e) => {
                  setLabel(e.target.value)
                  setDirty(true)
                }}
                className="w-full max-w-md rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
                placeholder="e.g. Spring 2026 tour positioning"
              />
            </div>

            <div className="mb-4 rounded-lg border border-ovation-ink/10 bg-white p-4">
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Companion PDF
              </div>
              {draft.pdfFilename ? (
                <p className="text-sm text-ovation-ink">
                  {draft.pdfFilename}
                  {draft.pdfUploadedAt && (
                    <span className="ml-2 text-ovation-muted">
                      uploaded {new Date(draft.pdfUploadedAt).toLocaleString()}
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-sm text-ovation-muted">
                  Optional PDF attached to dispatch emails alongside the web EPK link.
                </p>
              )}
              {canEdit && draft.status !== 'archived' && (
                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5">
                  <FileUp className="h-3.5 w-3.5" />
                  {pdfUploading ? 'Uploading…' : draft.pdfFilename ? 'Replace PDF' : 'Upload PDF'}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    disabled={pdfUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) void uploadPdf(file)
                      e.target.value = ''
                    }}
                  />
                </label>
              )}
              {pdfErr && <p className="mt-2 text-sm text-red-600">{pdfErr}</p>}
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div className="flex flex-col gap-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-ovation-muted">
                    HTML body
                  </label>
                  <textarea
                    value={htmlBody}
                    readOnly={readOnly}
                    onChange={(e) => {
                      setHtmlBody(e.target.value)
                      setDirty(true)
                    }}
                    rows={16}
                    className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-xs"
                    spellCheck={false}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-ovation-muted">
                    CSS (scoped under .epk)
                  </label>
                  <textarea
                    value={cssBody}
                    readOnly={readOnly}
                    onChange={(e) => {
                      setCssBody(e.target.value)
                      setDirty(true)
                    }}
                    rows={8}
                    className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 font-mono text-xs"
                    spellCheck={false}
                  />
                </div>
                <p className="text-xs text-ovation-muted">
                  Use {'{{ params.contactName }}'}, {'{{ params.monthlyListeners }}'}, etc.
                  Tranche params are merged at dispatch time.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ovation-muted">
                  Preview (sample params)
                </label>
                <iframe
                  title="EPK preview"
                  srcDoc={previewSrc}
                  className="h-[520px] w-full rounded-md border border-ovation-ink/15 bg-white"
                  sandbox=""
                />
              </div>
            </div>

            {draft.authorisedAt && (
              <p className="mt-4 text-xs text-ovation-muted">
                Authorised {new Date(draft.authorisedAt).toLocaleString()}
                {draft.authorisedBy ? ` by ${draft.authorisedBy}` : ''}
              </p>
            )}
          </>
        )}
      </AsyncBoundary>
    </div>
  )
}
